import React from "react";
import moment from "moment";
import { Chip } from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";

import { financeiroStyles, toneColor as baseToneColor } from "../Financeiro/financeiroShared";

// Peças compartilhadas das telas de Campanhas (mesmo visual de Contatos e
// Financeiro): estilos, selos de status, barra de progresso e estimativas.

export { KpiStrip, downloadCsv } from "../Financeiro/financeiroShared";

const PURPLE = { light: "#6a1b9a", dark: "#ce93d8" };

export const toneColor = (theme, tone) =>
  tone === "purple"
    ? PURPLE[theme.palette.type === "dark" ? "dark" : "light"]
    : baseToneColor(theme, tone);

// "Cancelada" aparece como "Pausada" porque pode ser retomada; "Inativa"
// aparece como "Rascunho". O valor no banco não muda.
// "Sem créditos" é a pausa automática quando o saldo de créditos acaba no
// meio do envio: vermelho, e não âmbar como a pausa manual, porque pede uma
// ação (recarga) antes de retomar.
export const STATUS = {
  INATIVA: { label: "Rascunho", tone: "grey" },
  PROGRAMADA: { label: "Programada", tone: "blue" },
  EM_ANDAMENTO: { label: "Enviando", tone: "green" },
  PAUSADA_SEM_CREDITO: { label: "Sem créditos", tone: "red" },
  CANCELADA: { label: "Pausada", tone: "amber" },
  FINALIZADA: { label: "Finalizada", tone: "purple" },
};

// Campanhas interrompidas, que podem ser editadas e retomadas. Na retomada, quem
// já recebeu não recebe de novo nem é cobrado outra vez.
export const isCampaignStopped = (status) =>
  status === "CANCELADA" || status === "PAUSADA_SEM_CREDITO";

export const STATUS_TABS = [
  { key: "", label: "Todas" },
  { key: "EM_ANDAMENTO", label: "Enviando" },
  { key: "PROGRAMADA", label: "Programadas" },
  { key: "INATIVA", label: "Rascunhos" },
  { key: "CANCELADA", label: "Pausadas" },
  { key: "PAUSADA_SEM_CREDITO", label: "Sem créditos" },
  { key: "FINALIZADA", label: "Finalizadas" },
];

const campaignStyles = (theme) => {
  const tint = (tone, a = 0.12) => ({
    color: toneColor(theme, tone),
    backgroundColor: alpha(toneColor(theme, tone), theme.palette.type === "dark" ? a + 0.06 : a),
  });
  const base = financeiroStyles(theme);
  return {
    ...base,
    // Em Campanhas a faixa de indicadores pode ter um 5º item (créditos, quando
    // o plano controla créditos). Colunas automáticas de mesma largura: com 4
    // itens fica igual às 4 colunas fixas do Financeiro; com 5, cabe numa linha.
    // No celular a faixa vira flex (regra herdada), e isto não se aplica.
    kpis: {
      ...base.kpis,
      gridTemplateColumns: "none",
      gridAutoFlow: "column",
      gridAutoColumns: "minmax(0, 1fr)",
    },
    pillPurple: tint("purple"),
    pillBlue: tint("blue"),
    statusDot: {
      display: "inline-block",
      width: 6,
      height: 6,
      borderRadius: "50%",
      marginRight: 6,
      backgroundColor: "currentColor",
    },
    tabCount: {
      marginLeft: 6,
      color: theme.palette.text.secondary,
      fontWeight: 600,
    },
    // botões compactos de ação na linha
    rowButton: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 8,
      height: 30,
      minWidth: 0,
      padding: theme.spacing(0, 1.5),
      whiteSpace: "nowrap",
    },
    rowButtonOutlined: {
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },
    tintGreen: { ...tint("green"), "&:hover": tint("green", 0.22) },
    tintAmber: { ...tint("amber"), "&:hover": tint("amber", 0.22) },
    tintBlue: {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.22) },
    },
    progressTrack: {
      display: "flex",
      height: 6,
      borderRadius: 999,
      overflow: "hidden",
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#ece9e2",
      marginTop: 4,
    },
    progressMeta: {
      display: "flex",
      justifyContent: "space-between",
      gap: 8,
      fontSize: 12,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    campaignName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      cursor: "pointer",
      background: "none",
      border: 0,
      padding: 0,
      font: "inherit",
      textAlign: "left",
      "&:hover": { textDecoration: "underline" },
    },
    warnText: { fontSize: 12, color: toneColor(theme, "amber") },
    backLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: "none",
      padding: theme.spacing(0.5, 1),
      marginLeft: -8,
      marginBottom: theme.spacing(1),
    },
    card: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 14,
    },
    cardTitle: { fontSize: 16, fontWeight: 600 },
    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      fontSize: 13,
      "& > span:first-child": { color: theme.palette.text.secondary },
      "& > span:last-child": { fontWeight: 600, textAlign: "right" },
    },
    messageBox: {
      whiteSpace: "pre-wrap",
      fontSize: 13,
      lineHeight: 1.45,
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 10,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f4f2ec",
      wordBreak: "break-word",
    },
    legendSwatch: {
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: 3,
      marginRight: 6,
      verticalAlign: "middle",
    },
  };
};

export const useCampaignStyles = makeStyles(campaignStyles);

export const statusPillClass = (status, classes) => {
  const tone = (STATUS[status] || STATUS.INATIVA).tone;
  return {
    green: classes.pillGreen,
    amber: classes.pillAmber,
    red: classes.pillRed,
    grey: classes.pillGrey,
    blue: classes.pillBlue,
    purple: classes.pillPurple,
  }[tone];
};

export const CampaignStatusPill = ({ status, classes, label }) => {
  const cfg = STATUS[status] || { label: status };
  return (
    <Chip
      size="small"
      label={
        <span>
          <span className={classes.statusDot} />
          {label || cfg.label}
        </span>
      }
      className={`${classes.pill} ${statusPillClass(status, classes)}`}
    />
  );
};

// Barra com até dois segmentos (ex.: confirmaram + entregues)
export const ProgressBar = ({ segments, classes, height }) => (
  <div className={classes.progressTrack} style={height ? { height } : undefined}>
    {segments.map((s, i) =>
      s.pct > 0 ? (
        <div key={i} style={{ width: `${Math.min(100, s.pct)}%`, backgroundColor: s.color }} />
      ) : null
    )}
  </div>
);

// ─── Números da campanha ─────────────────────────────────────────────────────

export const campaignNumbers = (c) => {
  const total = Number(c?.totalContacts || 0);
  const valid = Number(c?.validContacts || 0);
  const delivered = Number(c?.deliveredCount || 0);
  const confirmed = Number(c?.confirmedCount || 0);
  const requested = Number(c?.confirmationRequestedCount || 0);
  const pct = valid > 0 ? Math.round((delivered / valid) * 100) : 0;
  return { total, valid, invalid: Math.max(0, total - valid), delivered, confirmed, requested, pct, queued: Math.max(0, valid - delivered) };
};

// ─── Ritmo de envio ──────────────────────────────────────────────────────────

export const DEFAULT_PACE = { messageInterval: 20, longerIntervalAfter: 20, greaterInterval: 60 };

export const PACE_PRESETS = [
  { key: "fast", label: "Rápido", messageInterval: 5, longerIntervalAfter: 50, greaterInterval: 30 },
  { key: "recommended", label: "Recomendado", messageInterval: 20, longerIntervalAfter: 20, greaterInterval: 60 },
  { key: "careful", label: "Cauteloso", messageInterval: 40, longerIntervalAfter: 10, greaterInterval: 120 },
];

// Mesma conta do backend (buildCampaignSchedule em queues.ts)
export const estimateSeconds = (count, pace = DEFAULT_PACE) => {
  if (!count || count <= 1) return 0;
  const mi = Number(pace.messageInterval) || 0;
  const every = Number(pace.longerIntervalAfter) || 0;
  const pause = Number(pace.greaterInterval) || 0;
  const gaps = count - 1;
  const pauses = every > 0 ? Math.floor(gaps / every) : 0;
  return gaps * mi + pauses * pause;
};

export const formatDuration = (seconds) => {
  const s = Math.round(seconds || 0);
  if (s < 60) return s <= 0 ? "menos de 1 min" : `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
};

export const paceSummary = (pace) => {
  const mi = Number(pace.messageInterval) || 0;
  const every = Number(pace.longerIntervalAfter) || 0;
  const pause = Number(pace.greaterInterval) || 0;
  const base = mi > 0 ? `1 a cada ${mi} s` : "sem intervalo";
  return every > 0 && pause > 0 ? `${base} · pausa de ${formatDuration(pause)} a cada ${every}` : base;
};

// ─── Datas ───────────────────────────────────────────────────────────────────

// "Hoje, 14:00" / "Amanhã, 09:00" / "20/10, 10:00"
export const friendlyDateTime = (date) => {
  if (!date) return "—";
  const d = moment(date);
  const time = d.format("HH:mm");
  if (d.isSame(moment(), "day")) return `Hoje, ${time}`;
  if (d.isSame(moment().add(1, "day"), "day")) return `Amanhã, ${time}`;
  if (d.isSame(moment().subtract(1, "day"), "day")) return `Ontem, ${time}`;
  return d.isSame(moment(), "year") ? d.format("DD/MM, HH:mm") : d.format("DD/MM/YYYY, HH:mm");
};

// "em 18 h" / "em 25 min" / "há 2 dias"
export const fromNow = (date) => {
  if (!date) return "";
  const diffMin = moment(date).diff(moment(), "minutes");
  const abs = Math.abs(diffMin);
  let txt;
  if (abs < 60) txt = `${Math.max(1, abs)} min`;
  else if (abs < 60 * 48) txt = `${Math.round(abs / 60)} h`;
  else txt = `${Math.round(abs / (60 * 24))} dias`;
  return diffMin >= 0 ? `em ${txt}` : `há ${txt}`;
};

// Texto da mensagem com as variáveis trocadas por um exemplo
export const previewMessage = (text, contact, variables = []) => {
  let out = text || "";
  const c = contact || { name: "Mariana Souza", number: "5511988124410", email: "mariana@email.com" };
  out = out.replace(/{nome}/g, c.name || "").replace(/{numero}/g, c.number || "").replace(/{email}/g, c.email || "");
  (variables || []).forEach((v) => {
    if (!v?.key) return;
    out = out.split(`{${v.key}}`).join(v.value || "");
  });
  return out;
};

export const MESSAGE_FIELDS = ["message1", "message2", "message3", "message4", "message5"];
export const CONFIRMATION_FIELDS = [
  "confirmationMessage1",
  "confirmationMessage2",
  "confirmationMessage3",
  "confirmationMessage4",
  "confirmationMessage5",
];

// Lê /campaign-settings (lista de { key, value-em-JSON }) num objeto
export const parseCampaignSettings = (data) => {
  const out = { ...DEFAULT_PACE, variables: [] };
  (Array.isArray(data) ? data : []).forEach((item) => {
    try {
      out[item.key] = JSON.parse(item.value);
    } catch (e) {
      out[item.key] = item.value;
    }
  });
  if (!Array.isArray(out.variables)) out.variables = [];
  return out;
};
